import { useState, useEffect } from "react";
import { applyDateMask, validateDate } from "../utils/dateUtils";

const EMPTY = { company: "", position: "", startDate: "", endDate: "", isCurrentJob: false, activities: "" };
const NO_ERRORS = { company: "", position: "", startDate: "", endDate: "" };

// "YYYY-MM-DD" → "DD/MM/YYYY" by string, never via Date (avoids the UTC
// off-by-one that shifted every date one day back in BRT).
const isoToDisplay = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = String(dateStr).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

export const useExperienceForm = (t, initialExperiences = []) => {
  const [experience, setExperience] = useState(EMPTY);
  const [errors, setErrors] = useState(NO_ERRORS);
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    if (initialExperiences && initialExperiences.length > 0) {
      setExperiences(initialExperiences.map((exp) => ({
        ...exp,
        startDate: isoToDisplay(exp.startDate),
        endDate: exp.isCurrentJob ? "" : isoToDisplay(exp.endDate),
        id: exp.id || Date.now() + Math.random(),
      })));
    }
  }, [initialExperiences]);

  const handleExperienceChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "startDate" || name === "endDate") {
      const maskedValue = applyDateMask(value);
      setExperience((prev) => ({ ...prev, [name]: maskedValue }));
      setErrors((prev) => ({
        ...prev,
        [name]: maskedValue.length === 10 && !validateDate(maskedValue) ? t("Invalid date") : "",
      }));
      return;
    }

    setExperience((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (name === "isCurrentJob" && checked) {
      setExperience((prev) => ({ ...prev, endDate: "" }));
      setErrors((prev) => ({ ...prev, endDate: "" }));
    }
  };

  /** True when the user typed anything in the sub-form but did not add it. */
  const hasPendingInput = () =>
    Boolean(
      experience.company.trim() || experience.position.trim() || experience.startDate.trim() ||
      experience.endDate.trim() || experience.activities.trim() || experience.isCurrentJob,
    );

  const validateExperience = () => {
    const next = { ...NO_ERRORS };
    if (!experience.company.trim()) next.company = t("Company name is required");
    if (!experience.position.trim()) next.position = t("Position is required");
    if (!experience.startDate.trim()) next.startDate = t("Start date is required");
    else if (experience.startDate.length !== 10) next.startDate = t("Invalid date format");
    else if (!validateDate(experience.startDate)) next.startDate = t("Invalid date");
    if (experience.endDate && !experience.isCurrentJob && !validateDate(experience.endDate)) next.endDate = t("Invalid date");
    return next;
  };

  /**
   * Validates and moves the sub-form into the list. Returns
   * { ok, experiences } synchronously so callers (e.g. the page's save
   * handler) can use the updated list in the same tick.
   */
  const addExperience = () => {
    const next = validateExperience();
    setErrors(next);
    if (Object.values(next).some(Boolean)) return { ok: false, experiences };

    const updated = [...experiences, { ...experience, id: Date.now() }];
    setExperiences(updated);
    setExperience(EMPTY);
    setErrors(NO_ERRORS);
    return { ok: true, experiences: updated };
  };

  const clearPending = () => {
    setExperience(EMPTY);
    setErrors(NO_ERRORS);
  };

  const removeExperience = (id) => {
    setExperiences((prev) => prev.filter((exp) => exp.id !== id));
  };

  return {
    experience,
    experiences,
    errors,
    handleExperienceChange,
    addExperience,
    removeExperience,
    hasPendingInput,
    clearPending,
  };
};
